import {
  useState, useEffect, useContext, useCallback,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';

import socketInstance from 'utils/socket.io';

import { getOffers, getCounterOffer, getMachineSoldPrice } from 'models/marketplace';
import { getMachine } from 'models/machinery';

import { UserContext } from 'contexts/userContext';
import { StatusContext } from 'contexts/statusContext';
import { SET_LOADING_STATUS } from 'contexts/constants/statusConstants';

import {
  COUNTER_OFFER_STATUS, MACHINE_STATE, SOCKET_EMIT, SOCKET_LISTEN, USER_TYPE,
} from 'constants/common';

const useMachineryHandler = ({ machine }) => {
  const { userData: user, isLoggedIn } = useContext(UserContext);
  const { dispatch: statusDispatch, statusData } = useContext(StatusContext);

  const [machineData, setMachineData] = useState(machine);
  const [contactWithCustomerModalVisible, setContactWithCustomerModalVisible] = useState(false);
  const [machineSoldPrice, setMachineSoldPrice] = useState();
  const [counterOfferData, setCounterOfferData] = useState({});
  const [offers, setOffers] = useState([]);
  const [minimumIncrement, setMinimumIncrement] = useState(0);
  const [offerPlacedResponse, setOfferPlacedResponse] = useState();
  const [counterOfferPlacedResponse, setCounterOfferPlacedResponse] = useState();

  const contactWithCustomerModalHandler = useCallback(() => {
    let contactWithCustomerModalTimeout;
    const modalVisibleFromStorage = localStorage.getItem('contactWithCustomerModalVisible');

    if (!modalVisibleFromStorage && !isLoggedIn) {
      contactWithCustomerModalTimeout = setTimeout(() => {
        setContactWithCustomerModalVisible(true);
        localStorage.setItem('contactWithCustomerModalVisible', 'yes');
      }, 30000);
    }

    return () => contactWithCustomerModalTimeout && clearTimeout(contactWithCustomerModalTimeout);
  }, [isLoggedIn]);

  const getOldOffers = useCallback(async () => {
    try {
      const { offers: oldOffers, minimumIncrement: minInc, highestOffer } = await getOffers(machine.id);

      const userLatestOffer = find(oldOffers, (oldOffer) => oldOffer.userId === user?.id && !oldOffer.counteredReferance);
      if (userLatestOffer) {
        const { lastCounterOffer, counterOffers } = await getCounterOffer(userLatestOffer._id);
        const userMaxOffer = counterOffers.reverse().find((counterOffer) => counterOffer.counteredBy === USER_TYPE.BUYER && counterOffer.offer > userLatestOffer.offer);

        if (lastCounterOffer && lastCounterOffer.counteredBy === USER_TYPE.SELLER && lastCounterOffer.status === COUNTER_OFFER_STATUS.PENDING) {
          if (userMaxOffer) {
            setCounterOfferData({
              lastOfferId: userLatestOffer._id,
              userLastOffer: userMaxOffer.offer,
              maxOffer: highestOffer,
              ...lastCounterOffer,
            });
          } else {
            setCounterOfferData({
              lastOfferId: userLatestOffer._id,
              userLastOffer: userLatestOffer.offer,
              maxOffer: highestOffer,
              ...lastCounterOffer,
            });
          }
        }
      } else {
        setCounterOfferData({});
      }

      setOffers(oldOffers);
      setMinimumIncrement(minInc);
    } catch (error) {
      console.log('getOldOffers', error);
    }
  }, [machine.id, user?.id]);

  const createContent = useCallback(async () => {
    statusDispatch({
      type: SET_LOADING_STATUS,
      payload: true,
    });
    setMachineData(machine);
    await getOldOffers();
    const soldPrice = machine.state === MACHINE_STATE.SOLD ? await getMachineSoldPrice(machine.id) : 0;
    setMachineSoldPrice(soldPrice);
    statusDispatch({
      type: SET_LOADING_STATUS,
      payload: false,
    });
  }, [machine, getOldOffers]);

  const updateMachine = useCallback(async () => {
    try {
      const { machine: machineFromApi } = await getMachine(machine.id);
      setMachineData(machineFromApi);
    } catch (error) {
      console.log('updateMachineErr', error);
    }
  }, [machine.id]);

  useEffect(() => {
    contactWithCustomerModalHandler();
  }, [contactWithCustomerModalHandler]);

  useEffect(() => {
    createContent();
  }, [createContent]);

  useEffect(() => {
    const { socket } = socketInstance;
    if (!isEmpty(socket) && statusData.socketConnected && machine?.id) {
      socket.emit(SOCKET_EMIT.JOIN_ROOM, machine.id);

      const onOfferPlaced = () => {
        console.log(`Socket event received: ${SOCKET_LISTEN.OFFER_PLACED}`);
        getOldOffers();
        updateMachine();
      };
      socket.on(SOCKET_LISTEN.OFFER_PLACED, onOfferPlaced);

      const onOfferCountered = () => {
        console.log(`Socket event received: ${SOCKET_LISTEN.OFFER_COUNTERED}`);
        getOldOffers();
        updateMachine();
      };
      socket.on(SOCKET_LISTEN.OFFER_COUNTERED, onOfferCountered);

      if (user?.id) {
        socket.emit(SOCKET_EMIT.JOIN_ROOM, `${machine.id}#${user?.id}`);

        const onOfferPlacedResponse = (data) => {
          console.log(`Socket event received: ${SOCKET_LISTEN.OFFER_PLACED_RESPONSE}`);
          setOfferPlacedResponse(data);
        };
        socket.on(SOCKET_LISTEN.OFFER_PLACED_RESPONSE, onOfferPlacedResponse);

        const onCounterOfferPlacedResponse = (data) => {
          console.log(`Socket event received: ${SOCKET_LISTEN.COUNTER_OFFER_PLACED_RESPONSE}`);
          setCounterOfferPlacedResponse(data);
        };
        socket.on(SOCKET_LISTEN.COUNTER_OFFER_PLACED_RESPONSE, onCounterOfferPlacedResponse);

        return () => {
          socket.emit(SOCKET_EMIT.LEAVE_ROOM, `${machine.id}#${user?.id}`);
          socket.off(SOCKET_LISTEN.OFFER_PLACED_RESPONSE, onOfferPlacedResponse);
          socket.off(SOCKET_LISTEN.COUNTER_OFFER_PLACED_RESPONSE, onCounterOfferPlacedResponse);
          socket.emit(SOCKET_EMIT.LEAVE_ROOM, machine.id);
          socket.off(SOCKET_LISTEN.OFFER_PLACED, onOfferPlaced);
          socket.off(SOCKET_LISTEN.OFFER_COUNTERED, onOfferCountered);
        };
      }

      return () => {
        socket.emit(SOCKET_EMIT.LEAVE_ROOM, machine.id);
        socket.off(SOCKET_LISTEN.OFFER_PLACED, onOfferPlaced);
        socket.off(SOCKET_LISTEN.OFFER_COUNTERED, onOfferCountered);
      };
    }

    return () => {};
  }, [machine, statusData.socketConnected, user]);

  return {
    machine: machineData,
    contactWithCustomerModalVisible,
    machineSoldPrice,
    counterOfferData,
    offers,
    minimumIncrement,
    offerPlacedResponse,
    counterOfferPlacedResponse,
    setOfferPlacedResponse,
    setCounterOfferPlacedResponse,
    setCounterOfferData,
    setContactWithCustomerModalVisible,
  };
};

export default useMachineryHandler;
